// Builds a full ActiveGameViewProps from a scenario's GameResponse fixture,
// filling every UI/callback/node prop with an inert default so the real screen
// renders faithfully with zero app data-flow. Dev-harness only — NOT app code.
import type { ActiveGameViewProps } from '../components/game/screen/ActiveGameView'
import type { GameResponse } from '@warcarp/api-types'
import type { HydratedGameState } from '../lib/gameState'
import { selectActivePhaseView } from '../lib/gameScreenState'
import { DEV_MAPS } from './maps'

const noop = () => {}

export function buildActiveGameViewProps(
  game: GameResponse,
  opts?: { viewerSlot?: number },
): ActiveGameViewProps {
  const viewerSlot = opts?.viewerSlot ?? 1
  const myPlayerId = String(viewerSlot)

  const wire = game.state as unknown as HydratedGameState
  const map = DEV_MAPS[game.mapId as string]
  if (!map) throw new Error(`preview harness: no bundled map for mapId '${game.mapId}'`)

  // Hydrate the same way deserializeGameState does: overlay the wire slim
  // territory slice (ownerId/armies) onto the static MapDef territories, since
  // MapView renders ownership + troop badges from `map.territories`.
  const mutById = new Map(
    (wire.territories ?? []).map((t) => [t.id, { ownerId: t.ownerId, armies: t.armies }]),
  )
  const hydratedMap = {
    ...map,
    territories: map.territories.map((t) => {
      const mut = mutById.get(t.id)
      return mut ? { ...t, ownerId: mut.ownerId ?? '', armies: mut.armies ?? 0 } : t
    }),
  }
  const gameState: HydratedGameState = { ...wire, map: hydratedMap }

  const myPlayer = game.players.find((p) => p.slot === viewerSlot) ?? null
  const isMyTurn = wire.players?.[wire.turnIndex] === myPlayerId

  const slotByPlayerId: Record<string, number> = {}
  const nameBySlot: Record<string, string> = {}
  for (const p of game.players) {
    slotByPlayerId[String(p.slot)] = p.slot
    nameBySlot[String(p.slot)] = p.displayName
  }
  const playerDisplayName = (pid: string) => nameBySlot[pid] ?? `Player ${pid}`

  const activePhaseView = selectActivePhaseView({
    regionDetailsMode: false,
    isMyTurn,
    phase: wire.phase,
  })

  return {
    // Core data (from the fixture)
    game,
    gameState,
    myPlayer,
    myPlayerId,
    isMyTurn,
    slotByPlayerId,
    playerDisplayName,
    activePhaseView,
    // Preview harness: placement state isn't exercised — supply inert defaults.
    placementStack: [],
    onPlacementStackChange: () => {},
    placementStepMode: 1,
    onPlacementStepModeChange: () => {},

    // Layout — portrait phone default
    isLandscape: false,
    isDesktop: false,

    // Time-machine — live view
    historicalGameState: null,
    historicalEventIndex: null,
    inHistoricalView: false,
    onScrubEvent: noop,
    onReturnToLive: noop,
    onViewAtEvent: noop,

    // Region details overlay — off
    regionDetailsMode: false,
    focusedRegionId: null,
    focusedDetailTerritoryId: null,
    detailAdjacentIds: [],
    regionColorById: {},
    onDetailRegionPress: noop,
    onDetailTerritoryPress: noop,
    onDetailBackgroundPress: noop,
    regionDetailsResetSignal: 0,

    // Corner overlays — none
    regionToggleNode: null,
    regionToggleCorner: 'top-right',
    zoomToggleNode: null,
    zoomToggleCorner: 'bottom-right',

    // Zoom / pan — neutral
    zoomEnabled: false,
    zoomScale: 1,
    panOffset: { tx: 0, ty: 0 },
    onPanChange: noop,
    onZoomScaleChange: noop,

    // Misc chrome
    landscapeActionHeader: null,
    onOpenCardsModal: noop,
    viewerJoinedAt: Date.now(),
    mapRightOverlay: null,
    viewerUserId: myPlayer?.userId,
  }
}
